import { Fragment, useCallback, useEffect, useState } from "react";
import {
  dismissApplication,
  fetchActivity,
  formatDateTime,
  registerApplication,
} from "../../services/adminService";
import { useInbox } from "./Inbox";
import { useToast } from "./Toast";
import RawDetails from "./RawDetails";

export default function SubmissionActivity() {
  const { apps, ready, errors } = useInbox();
  const toast = useToast();
  const [activity, setActivity] = useState([]);
  const [showHandled, setShowHandled] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const loadLog = useCallback(async () => {
    try {
      setActivity(await fetchActivity());
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  const waiting = apps.filter((a) => !a.registered && !a.dismissed);
  const shown = showHandled ? apps : waiting;

  const register = async (app) => {
    setBusyId(app.id);
    try {
      await registerApplication(app);
      toast.show(`${app.fullName} added to the register`);
      loadLog();
    } catch (err) {
      console.error(err);
      toast.show("Could not add this applicant to the register", "error");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (app) => {
    if (!window.confirm(`Dismiss the application from ${app.fullName}? It will be hidden from this list.`)) return;
    setBusyId(app.id);
    try {
      await dismissApplication(app);
      toast.show(`Application from ${app.fullName} dismissed`);
      loadLog();
    } catch (err) {
      console.error(err);
      toast.show("Could not dismiss this application", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <header className="na-page-head">
        <div>
          <h1>Submission activity</h1>
          <p className="na-muted">Membership applications from the website appear here the moment they are submitted.</p>
        </div>
        <div className="na-head-actions">
          <label className="na-inline na-small">
            <input type="checkbox" checked={showHandled} onChange={(e) => setShowHandled(e.target.checked)} />
            Show handled
          </label>
        </div>
      </header>

      {errors.apps && <p className="na-error">{errors.apps}</p>}

      <section className="na-card na-table-card">
        <h2 className="na-section-title">
          Website applications <span className="na-muted na-small">· {waiting.length} waiting</span>
        </h2>
        <div className="na-table-wrap">
          <table className="na-table">
            <thead>
              <tr><th>Applicant</th><th>Category</th><th>Organization</th><th>Submitted</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {!ready.apps && <tr><td colSpan="5" className="na-empty">Loading…</td></tr>}
              {ready.apps && shown.length === 0 && (
                <tr><td colSpan="5" className="na-empty">{showHandled ? "No applications yet." : "Nothing waiting. All applications are handled."}</td></tr>
              )}
              {shown.map((a) => (
                <Fragment key={a.id}>
                  <tr>
                    <td>
                      <strong>{a.fullName}</strong>
                      <small className="na-block">{[a.email, a.phone].filter(Boolean).join(" · ")}</small>
                    </td>
                    <td>{a.category || "—"}</td>
                    <td>{a.organization || "—"}</td>
                    <td>{formatDateTime(a.createdAt)}</td>
                    <td className="na-actions">
                      {a.registered ? (
                        <span className="na-badge na-badge-paid">On register</span>
                      ) : a.dismissed ? (
                        <span className="na-badge na-badge-unknown">Dismissed</span>
                      ) : (
                        <>
                          <button className="na-quick" disabled={busyId === a.id} onClick={() => register(a)}>
                            {busyId === a.id ? "Working…" : "Add to register"}
                          </button>
                          <button className="na-danger" disabled={busyId === a.id} onClick={() => dismiss(a)}>Dismiss</button>
                        </>
                      )}
                      <button onClick={() => setOpenId(openId === a.id ? null : a.id)}>
                        {openId === a.id ? "Hide" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {openId === a.id && (
                    <tr className="na-detail-row">
                      <td colSpan="5"><RawDetails data={a.raw} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="na-card na-table-card">
        <h2 className="na-section-title">Admin activity</h2>
        <ul className="na-log">
          {activity.length === 0 && <li className="na-empty">No activity recorded yet.</li>}
          {activity.map((l) => (
            <li key={l.id}>
              <span>{l.message}</span>
              <small>{l.actor} · {formatDateTime(l.createdAt)}</small>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
